'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';

import { CheckedImage } from '@/assets';
import type { PricingPlan } from '@/config/pricingPlans';
import { currency } from '@/utils/Parsers';

import IconButton from './IconButton';

const getTimeUnit = (unit: string) => {
  switch (unit) {
    case 'D':
      return 'ngày';
    case 'W':
      return 'tuần';
    case 'M':
      return 'tháng';
    case 'Y':
      return 'năm';
    default:
      return 'tháng';
  }
};

interface PricingPlanProp {
  title: string;
  features: string[];
  pricingPlans: PricingPlan[];
}

export const PricingPlans = ({
  title,
  features,
  pricingPlans,
}: PricingPlanProp) => {
  return (
    <div className="mx-auto">
      <div className="mt-12 flex flex-col-reverse gap-10 lg:flex-row">
        {pricingPlans.map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            whileHover={{ y: -10 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{
              duration: 0.6,
              delay: i * 0.1,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <div
              key={i}
              className={`flex max-w-md flex-col gap-10 rounded-3xl border bg-white p-5 shadow-lg shadow-primary-200/40 hover:shadow-primary-200/80 md:p-10 ${item.highlight ? 'border-2 border-primary-500' : 'md:scale-90'}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-primary-500 drop-shadow-md">{item.name}</h3>
                {item.highlight && (
                  <span className="rounded-full bg-primary-100 px-3 py-1 font-bold text-primary-500">
                    Tiết kiệm
                  </span>
                )}
              </div>
              <p className="text-left">{item.desc}</p>
              <div>
                <span className="mr-2 inline-block text-3xl font-bold md:text-4xl lg:text-5xl">{`${currency(+item.price)}₫`}</span>
                <span className="inline-block font-medium text-gray-700">{`/${item.duration} ${getTimeUnit(item.time_unit)}`}</span>
              </div>
              <Link href="/#download" className="w-full">
                <IconButton
                  label="Miễn phí 3 tháng"
                  disableTranslate
                  className="button-shinny w-full font-bold"
                />
              </Link>
              <ul>
                {features.map((feature, key) => (
                  <li key={key} className="flex items-start gap-4 py-2">
                    <Image src={CheckedImage} alt="check" />
                    <p className="text-left">{feature}</p>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};
